import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { fetchCollections, fetchCollectionProducts } from '../services/shopService';
import IconButton from './IconButton';
import ConfirmModal from './ConfirmModal';
import CategoryPanel from './CategoryPanel';
import './comic.css';

const selectStyle = {
  display: "block",
  width: "100%",
  height: "40px",
  margin: "8px 0",
  padding: "6px 10px",
  fontSize: "14px",
  borderRadius: "8px",
  border: "1px solid #ccc",
  fontFamily: "'Anonymous Pro', monospace"
};

const stepperBtnStyle = {
  width: "22px",
  height: "22px",
  background: "none",
  border: "1px solid #232526",
  borderRadius: "50%",
  fontSize: "13px",
  lineHeight: 1,
  cursor: "pointer",
  color: "#232526"
};

export const formatMoney = (amount, currencyCode) => {
  const value = Number(amount);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currencyCode }).format(value);
  } catch (e) {
    // Unrecognized currency code -- fall back to a plain numeric label
    // rather than letting Intl throw and blank the whole panel.
    return `${value.toFixed(2)} ${currencyCode}`;
  }
};

// One product card: shows the featured image, a variant picker (only when
// the product actually has more than the single Shopify-default "Default
// Title" variant -- most products won't need one shown at all), and an
// Add to Cart button. Selected-variant state lives here, per card, since
// each product picks independently of its siblings.
const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const hasRealVariants = !(product.variants.length === 1 && product.variants[0].title === "Default Title");
  const firstAvailable = product.variants.find((v) => v.availableForSale) || product.variants[0];

  const [selectedVariantId, setSelectedVariantId] = useState(firstAvailable?.id || "");
  const [adding, setAdding] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const selectedVariant = product.variants.find((v) => v.id === selectedVariantId) || firstAvailable;
  const soldOut = !selectedVariant || !selectedVariant.availableForSale;

  const handleAdd = async () => {
    if (!selectedVariant || adding) return;
    setAdding(true);
    setFeedback(null);
    const result = await addToCart(selectedVariant.id, 1);
    setAdding(false);
    if (result.success) {
      setFeedback({ ok: true, message: "Added to cart!" });
    } else {
      setFeedback({ ok: false, message: "Couldn't add that. Try again." });
    }
    setTimeout(() => setFeedback(null), 3000);
  };

  return (
    <div className="comic-panel comic-panel--entry" style={{ padding: "12px" }}>
      {product.featuredImage && (
        <img
          src={product.featuredImage.url}
          alt={product.featuredImage.altText || product.title}
          style={{ width: "100%", height: "320px", objectFit: "cover", borderRadius: "6px", marginBottom: "8px" }}
        />
      )}
      <div style={{ fontFamily: "'Anonymous Pro', monospace", fontWeight: "bold", color: "#232526", fontSize: "15px" }}>
        {product.title}
      </div>
      <div style={{ fontFamily: "'Anonymous Pro', monospace", color: "#232526", fontSize: "14px", margin: "2px 0 4px 0" }}>
        {selectedVariant
          ? formatMoney(selectedVariant.price.amount, selectedVariant.price.currencyCode)
          : formatMoney(product.priceRange.minVariantPrice.amount, product.priceRange.minVariantPrice.currencyCode)}
      </div>

      {hasRealVariants && (
        <select
          style={selectStyle}
          value={selectedVariantId}
          onChange={(e) => setSelectedVariantId(e.target.value)}
        >
          {product.variants.map((v) => (
            <option key={v.id} value={v.id} disabled={!v.availableForSale}>
              {v.title}{!v.availableForSale ? " (Sold out)" : ""}
            </option>
          ))}
        </select>
      )}

      <button
        type="button"
        onClick={handleAdd}
        disabled={soldOut || adding}
        className="comic-btn comic-btn--go"
        style={{ marginTop: "6px" }}
      >
        {soldOut ? "Sold Out" : adding ? "Adding..." : "Add to Cart"}
      </button>

      {feedback && (
        <div className={feedback.ok ? "comic-alert-bubble" : "comic-alert-bubble"} style={{ marginTop: "6px" }}>
          {feedback.message}
        </div>
      )}
    </div>
  );
};

// One collection tile: image + title, tappable to drill into that
// collection's products. Deliberately no product count/preview -- keeping
// it a plain title+image card matches the visual weight of a ProductCard
// without implying it's itself purchasable.
const CollectionCard = ({ collection, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(collection)}
    className="comic-panel comic-panel--entry comic-tap"
    style={{ padding: "12px", textAlign: "left", cursor: "pointer" }}
  >
    {collection.image && (
      <img
        src={collection.image.url}
        alt={collection.image.altText || collection.title}
        style={{ width: "100%", height: "280px", objectFit: "cover", borderRadius: "6px", marginBottom: "8px" }}
      />
    )}
    <div style={{ fontFamily: "'Anonymous Pro', monospace", fontWeight: "bold", color: "#232526", fontSize: "15px" }}>
      {collection.title}
    </div>
  </button>
);

const LoadMoreButton = ({ onClick, loading }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className="comic-btn"
    style={{ marginTop: "12px" }}
  >
    {loading ? "Loading..." : "Load More"}
  </button>
);

export const CartLine = ({ line }) => {
  const { updateLineQuantity, removeLine } = useCart();
  const [updating, setUpdating] = useState(false);
  // Confirmation for the explicit trash-icon removal only -- the "−"
  // stepper reaching 0 still removes the line immediately (standard
  // decrement-to-zero cart behavior, not a "delete button" tap), per the
  // 2026-10-12 app-wide rule scoped to delete-icon taps specifically.
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const handleQuantityChange = async (nextQuantity) => {
    if (updating) return;
    setUpdating(true);
    // Storefront API rejects a quantity of 0 on cartLinesUpdate -- dropping
    // to 0 means the user wants the line gone, so remove it outright instead.
    if (nextQuantity <= 0) {
      await removeLine(line.id);
    } else {
      await updateLineQuantity(line.id, nextQuantity);
    }
    setUpdating(false);
  };

  const handleConfirmRemove = async () => {
    if (updating) return;
    setUpdating(true);
    await removeLine(line.id);
    setUpdating(false);
    setConfirmingRemove(false);
  };

  return (
    <div className="stamp-row" style={{ alignItems: "center" }}>
      <span className="stamp-row__text">
        {line.merchandise.product.title}
        {line.merchandise.title !== "Default Title" ? ` (${line.merchandise.title})` : ""}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <button
            type="button"
            onClick={() => handleQuantityChange(line.quantity - 1)}
            disabled={updating}
            aria-label={`Decrease quantity of ${line.merchandise.product.title}`}
            style={stepperBtnStyle}
          >
            −
          </button>
          <span style={{ fontFamily: "'Anonymous Pro', monospace", fontSize: "13px", minWidth: "18px", textAlign: "center" }}>
            {line.quantity}
          </span>
          <button
            type="button"
            onClick={() => handleQuantityChange(line.quantity + 1)}
            disabled={updating}
            aria-label={`Increase quantity of ${line.merchandise.product.title}`}
            style={stepperBtnStyle}
          >
            +
          </button>
        </span>
        <span style={{ fontFamily: "'Anonymous Pro', monospace", fontSize: "13px", minWidth: "60px", textAlign: "right" }}>
          {formatMoney(line.cost.totalAmount.amount, line.cost.totalAmount.currencyCode)}
        </span>
        <IconButton
          icon="delete"
          onClick={() => setConfirmingRemove(true)}
          disabled={updating}
          color="#232526"
          label={`Remove ${line.merchandise.product.title} from cart`}
        />
      </div>

      {confirmingRemove && (
        <ConfirmModal
          title="Remove Item?"
          message={`Remove "${line.merchandise.product.title}" from your cart?`}
          confirmLabel="Remove"
          confirming={updating}
          onConfirm={handleConfirmRemove}
          onCancel={() => setConfirmingRemove(false)}
        />
      )}
    </div>
  );
};

// One collection/product per row, not a multi-column grid -- per direct
// user request, so each photo (collection tile or product card) reads
// bigger. Shared by both the collections list and the products-within-a-
// collection list.
const LIST_STYLE = { display: "grid", gridTemplateColumns: "1fr", gap: "12px", marginTop: "12px" };

// `isActive` -- whether the Shop tab is the currently-selected one (passed
// down from App.js's activeTab state). Shop itself always stays mounted
// (tabs don't unmount each other, so in-progress state like a drilled-into
// collection or a cart update survives switching away and back); `isActive`
// only gates the lazy first-load fetch below and the floating "Back to
// Collections" button, which shouldn't float over a different tab's content.
const Shop = ({ isActive }) => {
  const { cart } = useCart();

  // Collections browse list -- order/membership comes entirely from the
  // curated "Pumpous App Shop" menu (see shopService.fetchCollections), a
  // short hand-picked list, so unlike products-within-a-collection this
  // never needs pagination.
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsError, setCollectionsError] = useState(null);

  // Drill-down: null selectedCollection means "show the collections grid";
  // set means "show that collection's products" instead.
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsLoadingMore, setProductsLoadingMore] = useState(false);
  const [productsError, setProductsError] = useState(null);
  const [productsCursor, setProductsCursor] = useState(null);
  const [productsHasNextPage, setProductsHasNextPage] = useState(false);

  useEffect(() => {
    if (!isActive || collections.length > 0 || collectionsLoading) return;
    setCollectionsLoading(true);
    fetchCollections().then((result) => {
      if (result.success) {
        setCollections(result.collections);
      } else {
        setCollectionsError("Couldn't load the shop right now. Try again later.");
      }
      setCollectionsLoading(false);
    });
    // Only re-runs if the Shop tab is switched to again after
    // collections/loading state resets -- deliberately not re-fetching once
    // collections have already loaded (collections.length > 0 guards that).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const handleSelectCollection = async (collection) => {
    setSelectedCollection(collection);
    setProducts([]);
    setProductsError(null);
    setProductsCursor(null);
    setProductsHasNextPage(false);
    setProductsLoading(true);
    const result = await fetchCollectionProducts(collection.handle);
    if (result.success) {
      setProducts(result.products);
      setProductsCursor(result.endCursor);
      setProductsHasNextPage(result.hasNextPage);
    } else {
      setProductsError("Couldn't load that collection right now. Try again later.");
    }
    setProductsLoading(false);
  };

  const handleBackToCollections = () => {
    setSelectedCollection(null);
  };

  const handleLoadMoreProducts = async () => {
    if (productsLoadingMore || !productsHasNextPage || !selectedCollection) return;
    setProductsLoadingMore(true);
    const result = await fetchCollectionProducts(selectedCollection.handle, productsCursor);
    if (result.success) {
      setProducts((prev) => [...prev, ...result.products]);
      setProductsCursor(result.endCursor);
      setProductsHasNextPage(result.hasNextPage);
    } else {
      setProductsError("Couldn't load more products right now. Try again later.");
    }
    setProductsLoadingMore(false);
  };

  return (
    <>
    <CategoryPanel
      mascotVariant="shop"
      outerClassName="comic-panel--category comic-panel--border-green comic-panel--no-top-margin"
      title={`Shop${cart && cart.totalQuantity > 0 ? ` (${cart.totalQuantity})` : ""}`}
    >
      {selectedCollection === null ? (
        <>
          {collectionsLoading && <p className="comic-empty">Loading the shop...</p>}
          {collectionsError && <div className="comic-alert-bubble">{collectionsError}</div>}
          {!collectionsLoading && !collectionsError && collections.length === 0 && (
            <p className="comic-empty">No collections in the shop yet. Check back soon.</p>
          )}

          {collections.length > 0 && (
            <div style={LIST_STYLE}>
              {collections.map((collection) => (
                <CollectionCard key={collection.id} collection={collection} onSelect={handleSelectCollection} />
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <h3 className="comic-title" style={{ fontSize: "18px", margin: "6px 0" }}>
            {selectedCollection.title}
          </h3>

          {productsLoading && <p className="comic-empty">Loading products...</p>}
          {productsError && <div className="comic-alert-bubble">{productsError}</div>}
          {!productsLoading && !productsError && products.length === 0 && (
            <p className="comic-empty">No products in this collection yet.</p>
          )}

          {products.length > 0 && (
            <div style={LIST_STYLE}>
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}

          {productsHasNextPage && (
            <LoadMoreButton onClick={handleLoadMoreProducts} loading={productsLoadingMore} />
          )}
        </>
      )}
    </CategoryPanel>

    {isActive && selectedCollection && (
      <button
        type="button"
        onClick={handleBackToCollections}
        aria-label="Back to Collections"
        className="comic-floating-back"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      </button>
    )}
    </>
  );
};

export default Shop;
